/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_PRODUCT_CONTENT */
import { Suspense, lazy } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { resolvePdpSections, type PdpSection } from '@/extensions/product-content/lib/pdp-sections';
import { useProductContentData } from '@/extensions/product-content/context/product-content-data-context';
import type { HtmlContent } from '@/extensions/product-content/lib/api/product-content.server';

const ProductAdapterSection = lazy(() => import('@/extensions/product-content/components/product-adapter-section'));

/**
 * Body for the PDP "Product Details" tab — the same adapter-backed material / usage / care /
 * specification copy that `pdp-collapsibles-target` renders on the bundle page, but laid out flat
 * (heading + content) instead of inside collapsible shells, since the tab itself already provides
 * the show/hide affordance.
 */
export default function PdpDetailsContentTarget() {
    const ctx = useProductContentData();
    const collapsiblesPromise = ctx?.pdpCollapsiblesPromise;
    const product = ctx?.product;

    if (!collapsiblesPromise || !product) return null;

    const sections = resolvePdpSections(product);
    if (sections.length === 0) return null;

    return (
        <Suspense fallback={<DetailsShells sections={sections} />}>
            <Await resolve={collapsiblesPromise} errorElement={null}>
                {(contents) => <ResolvedDetails sections={sections} contents={contents} />}
            </Await>
        </Suspense>
    );
}

function DetailsShells({ sections }: { sections: PdpSection[] }) {
    return (
        <div className="space-y-6">
            {sections.map((section) => (
                <div key={section.apiMethod} className="space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                </div>
            ))}
        </div>
    );
}

function hasHtml(content: HtmlContent | null | undefined): boolean {
    return Boolean(content?.html?.trim());
}

function ResolvedDetails({ sections, contents }: { sections: PdpSection[]; contents: Array<HtmlContent | null> }) {
    const { t } = useTranslation('product');
    // Sections without real content are dropped rather than shown with a placeholder: this tab
    // already carries the catalog description, so filler copy would read as product data.
    const resolved = sections
        .map((section, index) => ({ section, content: contents[index] }))
        .filter((entry): entry is { section: PdpSection; content: HtmlContent } => hasHtml(entry.content));

    if (resolved.length === 0) return null;

    // A single section is the product description itself — its heading would duplicate the tab
    // label, so it renders unheaded.
    const showHeadings = resolved.length > 1;

    return (
        <div className="space-y-6">
            {resolved.map(({ section, content }) => (
                <div key={section.apiMethod} className="space-y-2">
                    {showHeadings && (
                        <h3 className="text-base font-stjude-medium text-foreground">{t(section.labelKey)}</h3>
                    )}
                    <ProductAdapterSection content={content} />
                </div>
            ))}
        </div>
    );
}
